import { getDiscountTypeValue } from '../constants/DiscountType'
import { fromCents, toCents as amountToCents } from './cents'
import type { TypeInvoiceItem } from '../types/Invoice'
import type { TypeSaleItem } from '../types/Sale'

const NONE_DISCOUNT_TYPE = getDiscountTypeValue('None')
const PERCENTAGE_DISCOUNT_TYPE = getDiscountTypeValue('Percentage')
const FIXED_DISCOUNT_TYPE = getDiscountTypeValue('Fixed Amount')

const asAmount = (value: unknown): number => {
  if (value === '' || value === null || value === undefined) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** Same rounding as payment tendered checks; coerce unknown values to a finite amount first. */
export const toCents = (amount: unknown): number => amountToCents(asAmount(amount))
export { fromCents }

/** Gross line total stays unit price × quantity; discounts are stored separately. */
export const lineGrossAmount = (unitPrice: unknown, quantity: unknown): number =>
  asAmount(unitPrice) * asAmount(quantity)

export const isNoneDiscountType = (discountType: unknown): boolean =>
  discountType == null || discountType === '' || Number(discountType) === NONE_DISCOUNT_TYPE

/** True when the line applies a percentage or fixed discount with a non-zero value. */
export const isAppliedLineDiscount = (discountType: unknown, discountValue: unknown): boolean => {
  if (isNoneDiscountType(discountType)) return false
  const type = Number(discountType)
  if (type !== PERCENTAGE_DISCOUNT_TYPE && type !== FIXED_DISCOUNT_TYPE) return false
  return toCents(asAmount(discountValue)) > 0
}

/**
 * Calculated line discount in currency units.
 * Percentage uses the backend rule: round(line total cents × rate / 100) cents.
 * Fixed amount is the supplied value, normalized to cents.
 */
export const calculateLineDiscountAmount = (
  discountType: unknown,
  discountValue: unknown,
  lineGross: unknown,
): number => {
  if (!isAppliedLineDiscount(discountType, discountValue)) return 0

  const type = Number(discountType)
  const value = asAmount(discountValue)
  const grossCents = toCents(asAmount(lineGross))

  if (type === PERCENTAGE_DISCOUNT_TYPE) {
    return fromCents(Math.round((grossCents * value) / 100))
  }

  return fromCents(toCents(value))
}

export const lineNetAmount = (lineGross: unknown, discountAmount: unknown): number =>
  fromCents(Math.max(0, toCents(asAmount(lineGross)) - toCents(asAmount(discountAmount))))

type LineDiscountSource = {
  discount_amount?: unknown
  discount_type?: unknown
  discount_value?: unknown
  total_price?: unknown
  unit_price?: unknown
  quantity?: unknown
}

export const saleItemLineDiscountAmount = (item: LineDiscountSource): number => {
  const stored = asAmount(item.discount_amount)
  if (stored > 0) return stored
  const gross = asAmount(item.total_price) || lineGrossAmount(item.unit_price, item.quantity)
  return calculateLineDiscountAmount(item.discount_type, item.discount_value, gross)
}

export const sumLineDiscountAmounts = (items: TypeSaleItem[] | undefined | null): number => {
  if (!items?.length) return 0
  return fromCents(items.reduce((total, item) => total + toCents(saleItemLineDiscountAmount(item)), 0))
}

/** Payable = gross − header discount − line discounts − loyalty + tax. */
export const salePayableAmount = (
  gross: unknown,
  headerDiscount: unknown,
  lineDiscounts: unknown,
  loyalty: unknown,
  tax: unknown,
): number =>
  fromCents(
    toCents(asAmount(gross)) -
      toCents(asAmount(headerDiscount)) -
      toCents(asAmount(lineDiscounts)) -
      toCents(asAmount(loyalty)) +
      toCents(asAmount(tax)),
  )

/**
 * Sale-level discount in currency units for the given gross.
 * Percentage is of gross (not net of line discounts). None and zero are 0.
 * Callers that are adding a line must pass the post-add gross.
 */
export const headerDiscountAmount = (discountType: unknown, discountValue: unknown, saleGross: unknown): number => {
  if (isNoneDiscountType(discountType)) return 0
  const value = asAmount(discountValue)
  if (value === 0) return 0
  if (Number(discountType) === PERCENTAGE_DISCOUNT_TYPE) {
    return (asAmount(saleGross) * value) / 100
  }
  return value
}

/**
 * Header discount allocated to each line by gross weight.
 * Last weighted line receives leftover cents so shares sum to the header amount.
 */
export const headerDiscountShareAmounts = (headerDiscount: unknown, lineGrossAmounts: unknown[]): number[] => {
  const headerCents = toCents(asAmount(headerDiscount))
  const weightCents = lineGrossAmounts.map((amount) => Math.max(0, toCents(asAmount(amount))))
  const grossCents = weightCents.reduce((total, cents) => total + cents, 0)
  if (headerCents <= 0 || grossCents <= 0) {
    return weightCents.map(() => 0)
  }

  const weightedIndexes = weightCents.map((cents, index) => (cents > 0 ? index : -1)).filter((index) => index >= 0)
  const shares = weightCents.map(() => 0)
  if (weightedIndexes.length === 0) return shares

  let allocatedCents = 0
  const lastIndex = weightedIndexes[weightedIndexes.length - 1]
  for (const index of weightedIndexes) {
    if (index === lastIndex) {
      shares[index] = fromCents(headerCents - allocatedCents)
      continue
    }

    const shareCents = Math.round((headerCents * weightCents[index]) / grossCents)
    shares[index] = fromCents(shareCents)
    allocatedCents += shareCents
  }

  return shares
}

export const lineRemainingAfterHeaderShare = (lineGross: unknown, headerShare: unknown): number =>
  fromCents(Math.max(0, toCents(asAmount(lineGross)) - toCents(asAmount(headerShare))))

/**
 * Remaining room on a line that is about to be added.
 * Pass the header amount the sale will have after this add. When `mergeIndex`
 * hits an existing line, allocate on that merged gross and subtract that
 * line's current discount so existing + incoming cannot exceed remaining.
 */
export const appendedLineRemainingAfterHeaderShare = (
  headerDiscount: unknown,
  existingLineGrossAmounts: unknown[],
  incomingLineGross: unknown,
  mergeIndex = -1,
  existingLineDiscount = 0,
): number => {
  const existing = existingLineGrossAmounts.map((amount) => asAmount(amount))
  const incoming = asAmount(incomingLineGross)
  const mergesIntoExisting = mergeIndex >= 0 && mergeIndex < existing.length
  const lineGrossAmounts = mergesIntoExisting
    ? existing.map((gross, index) => (index === mergeIndex ? fromCents(toCents(gross) + toCents(incoming)) : gross))
    : [...existing, incoming]
  const shares = headerDiscountShareAmounts(headerDiscount, lineGrossAmounts)
  const targetIndex = mergesIntoExisting ? mergeIndex : lineGrossAmounts.length - 1
  const remainingAfterHeader = lineRemainingAfterHeaderShare(
    lineGrossAmounts[targetIndex] ?? 0,
    shares[targetIndex] ?? 0,
  )
  if (!mergesIntoExisting) return remainingAfterHeader

  return lineRemainingAfterHeaderShare(remainingAfterHeader, existingLineDiscount)
}

export const lineDiscountMessage = (
  discountType: unknown,
  discountValue: unknown,
  lineGross: unknown,
  remainingAfterHeaderShare?: unknown,
): string | null => {
  if (isNoneDiscountType(discountType) && toCents(asAmount(discountValue)) > 0) {
    return 'Choose a discount type before entering a discount value.'
  }

  if (!isAppliedLineDiscount(discountType, discountValue) && isNoneDiscountType(discountType)) {
    return null
  }

  if (isNoneDiscountType(discountType)) return null

  const type = Number(discountType)
  if (type !== PERCENTAGE_DISCOUNT_TYPE && type !== FIXED_DISCOUNT_TYPE) {
    return 'The discount type is invalid.'
  }

  const value = asAmount(discountValue)
  if (value < 0) return 'The discount value cannot be negative.'

  if (type === PERCENTAGE_DISCOUNT_TYPE && value > 100) {
    return 'The percentage discount cannot exceed 100.'
  }

  const gross = asAmount(lineGross)
  const remaining =
    remainingAfterHeaderShare === undefined || remainingAfterHeaderShare === null
      ? gross
      : asAmount(remainingAfterHeaderShare)
  const maxDiscountCents = Math.min(toCents(gross), Math.max(0, toCents(remaining)))
  const discountAmount = calculateLineDiscountAmount(discountType, discountValue, gross)
  if (toCents(discountAmount) > maxDiscountCents) {
    if (maxDiscountCents < toCents(gross)) {
      return `The line discount cannot exceed the line total minus the sale discount share (${fromCents(
        maxDiscountCents,
      ).toFixed(2)}).`
    }
    return 'The line discount cannot exceed the line total.'
  }

  return null
}

/**
 * First remaining-room error on lines already on the sale, after the current header discount.
 * Add-time remaining lives in SaleItemForm; save uses this same check.
 */
export const saleItemsLineDiscountMessage = (
  items: TypeSaleItem[] | undefined | null,
  headerDiscount: unknown,
): string | null => {
  if (!items?.length) return null

  const lineGrossAmounts = items.map(
    (item) => asAmount(item.total_price) || lineGrossAmount(item.unit_price, item.quantity),
  )
  const headerShares = headerDiscountShareAmounts(headerDiscount, lineGrossAmounts)

  for (const [index, item] of items.entries()) {
    const remaining = lineRemainingAfterHeaderShare(lineGrossAmounts[index], headerShares[index])
    const discountError = lineDiscountMessage(
      item.discount_type,
      item.discount_value,
      lineGrossAmounts[index],
      remaining,
    )
    if (discountError) {
      const label = item.item_name?.trim() || `Item #${index + 1}`
      return `${label}: ${discountError}`
    }
  }

  return null
}

/** Stamp calculated discount fields, or drop them when the line has no discount. */
export const applyLineDiscountSnapshot = (item: TypeSaleItem): TypeSaleItem => {
  const quantity = asAmount(item.quantity)
  const unitPrice = asAmount(item.unit_price)
  const totalPrice = asAmount(item.total_price) || lineGrossAmount(unitPrice, quantity)

  if (!isAppliedLineDiscount(item.discount_type, item.discount_value)) {
    return {
      ...item,
      total_price: totalPrice,
      discount_type: undefined,
      discount_value: undefined,
      discount_amount: undefined,
    }
  }

  return {
    ...item,
    total_price: totalPrice,
    discount_type: Number(item.discount_type),
    discount_value: asAmount(item.discount_value),
    discount_amount: calculateLineDiscountAmount(item.discount_type, item.discount_value, totalPrice),
  }
}

export const lineDiscountPayload = (
  item: TypeSaleItem,
): { discount_type: number; discount_value: number; discount_amount: number } | Record<string, never> => {
  const snapshot = applyLineDiscountSnapshot(item)
  if (!isAppliedLineDiscount(snapshot.discount_type, snapshot.discount_value)) {
    return {}
  }

  return {
    discount_type: Number(snapshot.discount_type),
    discount_value: asAmount(snapshot.discount_value),
    discount_amount: asAmount(snapshot.discount_amount),
  }
}

type InvoiceLineDiscountFields = Pick<TypeInvoiceItem, 'line_discount_amount'> & {
  financial?: Pick<TypeInvoiceItem['financial'], 'line_discount_amount'> | null
  total_price?: number
}

export const invoiceItemLineDiscountAmount = (item: InvoiceLineDiscountFields): number =>
  asAmount(item.line_discount_amount ?? item.financial?.line_discount_amount)

export const invoiceItemGrossAmount = (
  item: Pick<TypeInvoiceItem, 'total_amount'> & { total_price?: number },
): number => asAmount(item.total_amount ?? item.total_price)

/** Discounted line total before header discount, loyalty, and tax. */
export const invoiceItemNetAmount = (item: Pick<TypeInvoiceItem, 'total_amount'> & InvoiceLineDiscountFields): number =>
  lineNetAmount(invoiceItemGrossAmount(item), invoiceItemLineDiscountAmount(item))

export const sumInvoiceLineDiscounts = (items: InvoiceLineDiscountFields[] | undefined | null): number => {
  if (!items?.length) return 0
  return fromCents(items.reduce((total, item) => total + toCents(invoiceItemLineDiscountAmount(item)), 0))
}

export { NONE_DISCOUNT_TYPE, PERCENTAGE_DISCOUNT_TYPE, FIXED_DISCOUNT_TYPE }
